import { API_CONFIG, getAuthHeaders, handleApiError, ApiResponse } from './apiConfig'

// Helper pour fetch avec configuration CORS
function fetchWithCors(url: string, options: RequestInit = {}): Promise<Response> {
  return fetch(url, {
    ...options,
    mode: 'cors',
    credentials: 'include',
    headers: {
      ...API_CONFIG.headers,
      ...options.headers
    }
  })
}

// Interfaces
export interface CarteBanque {
  id: number
  nom: string
  slug: string
  description: string
  image: string | null
  imageWallet: string | null
  imageColor: string | null
}

export interface Carte {
  id: number
  reference: string
  banqueId: number
  banqueNom: string
  banqueSlug: string
  image: string | null
  imageWallet: string | null
  imageColor: string | null
  alias: string
  numeroMasque: string
  bloc4: string
  type: string
  expirationMois: string
  expirationAnnee: string
  solde: number
  repartitionPourcent: number
  repartitionMontant: number
  codeRetrait: string | null
  parDefaut: boolean
  statut: string
}

export interface CarteTransaction {
  id: number
  reference: string
  description: string
  categorieSlug: string
  montant: number
  type: string // 'credit' | 'debit'
  devise: string
  libelle: string
  walletId: number | null
  voirWallet: boolean
  statut: string
  date: string
  metadata: any
  userId: number | string
  userNom: string
}

export interface CarteCategorieStat {
  categorie_slug: string
  total: number
  pourcentage: number
}

export interface CarteMoisData {
  mois: number
  annee: number
  montant: number
  nombre: number
  stat: CarteCategorieStat[]
  transactions: CarteTransaction[]
}

export interface CarteDetailResponse {
  carte: Carte
  data: CarteMoisData[]
}

// Service Carte
const carteService = {

  // Récupérer la liste des cartes de l'utilisateur
  async getCartes(): Promise<ApiResponse<Carte[]>> {
    try {
      const response = await fetchWithCors(`${API_CONFIG.baseURL}/cartes`, {
        method: 'GET',
        headers: getAuthHeaders()
      })
      const data = await response.json()
      if (!response.ok) {
        return handleApiError({ response: { status: response.status, data } })
      }
      return { status: response.status, message: 'Cartes récupérées', data: data.data || data }
    } catch (error) {
      return handleApiError(error)
    }
  },

  // Récupérer le détail d'une carte avec transactions
  async getCarte(id: number): Promise<ApiResponse<CarteDetailResponse>> {
    try {
      const response = await fetchWithCors(`${API_CONFIG.baseURL}/cartes/${id}`, {
        method: 'GET',
        headers: getAuthHeaders()
      })
      const data = await response.json()
      if (!response.ok) {
        return handleApiError({ response: { status: response.status, data } })
      }
      return { status: response.status, message: 'Carte récupérée', data: { carte: data.carte, data: data.data } }
    } catch (error) {
      return handleApiError(error)
    }
  },

  // Récupérer les banques disponibles
  async getBanques(): Promise<ApiResponse<CarteBanque[]>> {
    try {
      const response = await fetchWithCors(`${API_CONFIG.baseURL}/cartes/banques`, {
        method: 'GET',
        headers: getAuthHeaders()
      })
      const data = await response.json()
      if (!response.ok) {
        return handleApiError({ response: { status: response.status, data } })
      }
      return { status: response.status, message: 'Banques récupérées', data: data.data || data }
    } catch (error) {
      return handleApiError(error)
    }
  },

  // Créer une nouvelle carte
  async createCarte(banqueId: number, alias: string): Promise<ApiResponse> {
    try {
      const response = await fetchWithCors(`${API_CONFIG.baseURL}/cartes`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ banqueId, alias })
      })
      const data = await response.json()
      if (!response.ok) {
        return handleApiError({ response: { status: response.status, data } })
      }
      return { status: response.status, message: data.message || 'Carte créée', data: data.data || data }
    } catch (error) {
      return handleApiError(error)
    }
  },

  // Modifier l'alias d'une carte
  async setAlias(carteId: number, alias: string): Promise<ApiResponse<Carte>> {
    try {
      const response = await fetchWithCors(`${API_CONFIG.baseURL}/cartes/alias`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ carteId, alias })
      })
      const data = await response.json()
      if (!response.ok) {
        return handleApiError({ response: { status: response.status, data } })
      }
      return { status: response.status, message: 'Alias modifié', data: data.carte || data }
    } catch (error) {
      return handleApiError(error)
    }
  },

  // Changer le statut d'une carte (bloquer/débloquer)
  async setStatut(carteId: number, statut: string): Promise<ApiResponse> {
    try {
      const response = await fetchWithCors(`${API_CONFIG.baseURL}/cartes/statut`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ carteId, statut })
      })
      const data = await response.json()
      if (!response.ok) {
        return handleApiError({ response: { status: response.status, data } })
      }
      return { status: response.status, message: 'Statut modifié', data: data }
    } catch (error) {
      return handleApiError(error)
    }
  },

  // Recharger la carte depuis le wallet
  async rechargerCarte(carteId: number, montant: number): Promise<ApiResponse> {
    try {
      const response = await fetchWithCors(`${API_CONFIG.baseURL}/cartes/transactions`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          carteId,
          montant,
          devise: 'F',
          type: 'credit',
          libelle: 'Recharge carte',
          categorieSlug: 'recharger'
        })
      })
      const data = await response.json()
      if (!response.ok) {
        return handleApiError({ response: { status: response.status, data } })
      }
      return { status: response.status, message: data.message || 'Recharge effectuée', data: data }
    } catch (error) {
      return handleApiError(error)
    }
  },

  // Répartition des fonds sur les cartes
  async setRepartition(cartes: { id: number; repartitionPourcent: number; repartitionMontant: number }[]): Promise<ApiResponse> {
    try {
      const response = await fetchWithCors(`${API_CONFIG.baseURL}/cartes/repartition`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ repartition: JSON.stringify({ cartes }) })
      })
      const data = await response.json()
      if (!response.ok) {
        return handleApiError({ response: { status: response.status, data } })
      }
      return { status: response.status, message: data.message || 'Répartition effectuée', data: data }
    } catch (error) {
      return handleApiError(error)
    }
  },

  // Activer une carte livrée
  async linkCarte(last4: string): Promise<ApiResponse> {
    try {
      const response = await fetchWithCors(`${API_CONFIG.baseURL}/cartes/link`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ last4 })
      })
      const data = await response.json()
      if (!response.ok) {
        return handleApiError({ response: { status: response.status, data } })
      }
      return { status: response.status, message: 'Carte activée', data: data }
    } catch (error) {
      return handleApiError(error)
    }
  }
}

export default carteService
